// src/app/router.js
// Router mínimo (SPA) para cargar pages HTML y sus controladores JS opcionales.
// - Carga /src/ui/pages/<Page>.html en #page-container
// - Si existe /src/ui/pages/<Page>.js lo importa dinámicamente
// - Auth guard: si no hay sesión -> login
// - Si hay sesión y ruta=login -> dashboard

import { getSupabaseClient } from "../services/supabaseClient.js";
import { getUser, getRole } from "../services/teamService.js";
import { getUserRole, homeRouteForRole, NO_ACCESS_ROLE } from "../services/rbac.js";
import { listMySupplierEvents } from "../services/eventSuppliersService.js";
import { getState } from "./state.js";


// Templates de paginas (bundled)
import dashboardHtml from "../ui/pages/Dashboard.html?raw";
import eventsHtml from "../ui/pages/Events.html?raw";
import productionHtml from "../ui/pages/Production.html?raw";
import mobileTodayHtml from "../ui/pages/MobileToday.html?raw";
import contactsHtml from "../ui/pages/Contacts.html?raw";
import venuesHtml from "../ui/pages/Venues.html?raw";
import financeHtml from "../ui/pages/Finance.html?raw";
import emailingHtml from "../ui/pages/Emailing.html?raw";
import loginHtml from "../ui/pages/Login.html?raw";
import settingsHtml from "../ui/pages/Settings.html?raw";
import supplierEventsHtml from "../ui/pages/SupplierEvents.html?raw";


const ROUTES = Object.freeze({
  dashboard: { html: dashboardHtml, jsLoader: () => import("../ui/pages/Dashboard.js"), auth: true, roles: ['admin', 'manager', 'viewer'] },
  events: { html: eventsHtml, jsLoader: () => import("../ui/pages/Events.js"), auth: true, roles: ['admin', 'manager'] },
  production: { html: productionHtml, jsLoader: () => import("../ui/pages/Production.js"), auth: true, roles: ['admin', 'manager'] },
  mobile: { html: mobileTodayHtml, jsLoader: () => import("../ui/pages/MobileToday.js"), auth: true, roles: ['admin', 'manager', 'viewer'] },
  contacts: { html: contactsHtml, jsLoader: () => import("../ui/pages/Contacts.js"), auth: true, roles: ['admin', 'manager'] },
  venues: { html: venuesHtml, jsLoader: () => import("../ui/pages/Venues.js"), auth: true, roles: ['admin', 'manager'] },
  finance: { html: financeHtml, jsLoader: () => import("../ui/pages/Finance.js"), auth: true, roles: ['admin', 'manager'] },
  emailing: { html: emailingHtml, jsLoader: () => import("../ui/pages/Emailing.js"), auth: true, roles: ['admin', 'manager'] },
  settings: { html: settingsHtml, jsLoader: () => import("../ui/pages/Settings.js"), auth: true, roles: ['admin', 'manager', 'viewer'] },
  // "Mis servicios" del proveedor. Un músico enlazado a su contacto de proveedor
  // (p. ej. sonido) también la tiene. Quien no tiene rol válido acaba aquí y no ve nada.
  supplier: { html: supplierEventsHtml, jsLoader: () => import("../ui/pages/SupplierEvents.js"), auth: true, roles: ['supplier', 'viewer', NO_ACCESS_ROLE] },

  login: { html: loginHtml, jsLoader: () => import("../ui/pages/Login.js"), auth: false },
});


const DEFAULT_AUTHED_ROUTE = "dashboard";
const DEFAULT_PUBLIC_ROUTE = "login";

export function isMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
}

// Landing por dispositivo: en móvil arrancamos en "Hoy" (próximos bolos); en escritorio, dashboard.
export function getDefaultAuthedRoute() {
  return isMobileViewport() ? "mobile" : DEFAULT_AUTHED_ROUTE;
}

let _isInitialized = false;
let _currentRoute = null;

function normalizeRole(role) {
  return String(role || NO_ACCESS_ROLE).trim().toLowerCase();
}

// Proveedor o sin rol válido: solo existe la página "Mis servicios".
function isSupplierOnly(role) {
  return role === "supplier" || role === NO_ACCESS_ROLE;
}

// ¿El músico tiene servicios como proveedor? Se vuelve a preguntar cada 5
// minutos o al cambiar de usuario; si falla, se repite en la siguiente navegación.
const SUPPLIER_SERVICES_TTL_MS = 5 * 60_000;
let _supplierServices = { userId: null, value: false, at: 0 };
async function viewerHasSupplierServices() {
  const user = await getUser().catch(() => null);
  if (!user) return false;
  const fresh = _supplierServices.userId === user.id && Date.now() - _supplierServices.at < SUPPLIER_SERVICES_TTL_MS;
  if (!fresh) {
    const rows = await listMySupplierEvents().catch(() => null);
    if (rows === null) return _supplierServices.userId === user.id && _supplierServices.value;
    _supplierServices = { userId: user.id, value: rows.length > 0, at: Date.now() };
  }
  return _supplierServices.value;
}

function ensureEl(selector, name = selector) {
  const el = document.querySelector(selector);
  if (!el) throw new Error(`No existe ${name} (${selector}) en el DOM.`);
  return el;
}



function safeDecodeURIComponent(v) {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

function getRouteFromLocation() {
  // soporta:
  // - /#/events
  // - /?route=events
  // - /events (en dev con fallback)
  const hash = window.location.hash || "";
  if (hash.startsWith("#/")) return safeDecodeURIComponent(hash.slice(2)).split("?")[0] || "";
  if (hash.startsWith("#")) return safeDecodeURIComponent(hash.slice(1)).split("?")[0] || "";

  const url = new URL(window.location.href);
  const q = url.searchParams.get("route");
  if (q) return safeDecodeURIComponent(q);

  // path fallback
  const path = url.pathname.replace(/^\/+/, "");
  return safeDecodeURIComponent(path) || "";
}

async function hasSession() {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getSession();
    if (error) return false;
    return Boolean(data?.session);
  } catch {
    return false;
  }
}

async function setActiveNav(routeName) {
  const mainNav = document.querySelector("#main-nav");
  const bottomNav = document.querySelector(".bottom-nav");
  if (!mainNav && !bottomNav) return;

  try {
    const role = await getUserRole();
    const normalizedRole = normalizeRole(role);
    
    // Obtener modo de experiencia activo
    const activeMode = getState().ui?.preferences?.experienceMode || "management";
    const showOnlyViewerPages = !isSupplierOnly(normalizedRole) && (normalizedRole === "viewer" || activeMode === "my_events");
    
    const navs = [mainNav, bottomNav].filter(Boolean);
    const showSupplierPage = isSupplierOnly(normalizedRole)
      || (normalizedRole === "viewer" && await viewerHasSupplierServices());
    // Si mientras tanto se ha navegado a otra página, ya la marcará su llamada.
    if (_currentRoute && routeName !== _currentRoute) return;
    document.querySelector("#mobile-quick-expense")?.classList.toggle("hidden", isSupplierOnly(normalizedRole));

    for (const nav of navs) {
      nav.querySelectorAll("[data-route]").forEach((btn) => {
        const btnRoute = btn.getAttribute("data-route");
        const routeConfig = ROUTES[btnRoute];

        // Ocultar si el rol no tiene permiso OR si estamos en modo "Mis Bolos" y es una página de manager
        const isAllowedForMode = !showOnlyViewerPages || ["dashboard", "settings", "mobile", "supplier"].includes(btnRoute);
        const hasPermission = routeConfig && routeConfig.roles && routeConfig.roles.includes(normalizedRole)
          && (btnRoute !== "supplier" || showSupplierPage);

        if (!hasPermission || !isAllowedForMode) {
          btn.classList.add("hidden");
        } else {
          btn.classList.remove("hidden");
        }

        if (btnRoute === routeName) btn.classList.add("is-active");
        else btn.classList.remove("is-active");
      });
    }
  } catch (e) {
    console.warn('[router] setActiveNav failed:', e);
  }
}

export async function refreshActiveNav() {
  return setActiveNav(_currentRoute || getRouteFromLocation() || DEFAULT_PUBLIC_ROUTE);
}


async function loadPage(routeName) {
  const route = ROUTES[routeName];
  if (!route) throw new Error(`Ruta desconocida: ${routeName}`);

  const container = ensureEl("#page-container", "#page-container");

  // 1) HTML (ya es string en route.html)
  // const html = await fetchText(route.html);
  container.innerHTML = route.html;

  // 1.1) Layout constraints (Events, Contacts, Venues, Finance not full width)
  const constrainedRoutes = ["events", "contacts", "venues", "finance"];
  if (constrainedRoutes.includes(routeName)) {
    container.classList.add("layout-constrained");
  } else {
    container.classList.remove("layout-constrained");
  }

  // 2) JS opcional
  if (route.jsLoader) {
    try {
      const mod = await route.jsLoader();

      // Si exporta default function o init(), lo ejecutamos
      if (typeof mod.default === "function") await mod.default();
      else if (typeof mod.init === "function") await mod.init();
    } catch (e) {
      console.warn(`[router] No se pudo cargar JS de ${routeName}.`, e);
      // Intentar mostrar error en UI si estamos en dev/debug
      // alert("Error cargando script de página: " + e.message);
    }
  }
}

async function applyAuthGuards(targetRoute) {
  const route = ROUTES[targetRoute];
  if (!route) return DEFAULT_PUBLIC_ROUTE;

  try {
    const sessionUser = await getUser();
    const sessionOk = !!sessionUser;

    // si la ruta requiere auth y no hay sesión -> login
    if (route.auth && !sessionOk) {
      return DEFAULT_PUBLIC_ROUTE;
    }

    // si estás en login y ya hay sesión -> dashboard/events según rol
    if (!route.auth && targetRoute === "login" && sessionOk) {
      const role = await getUserRole();
      const normalizedRole = normalizeRole(role);
      const defaultRoute = homeRouteForRole(normalizedRole, isMobileViewport());
      console.log(`[router] Guard: login page but session exists -> ${defaultRoute}`);
      return defaultRoute;
    }

    // Verificar ROLES
    if (route.auth && route.roles) {
      const role = await getUserRole();
      const normalizedRole = normalizeRole(role);
      if (!route.roles.includes(normalizedRole)) {
        console.warn(`[router] Access denied for role ${normalizedRole} to route ${targetRoute}`);
        return homeRouteForRole(normalizedRole, isMobileViewport());
      }
    }

    return targetRoute;
  } catch (e) {
    console.warn('[router] Auth guard failed, redirecting to login:', e);
    return DEFAULT_PUBLIC_ROUTE; // safe fallback to login
  }
}

// Helper to hide/show the main sidebar based on auth state
function toggleSidebarVisibility(show) {
  const sidebar = document.getElementById("main-sidebar");
  if (!sidebar) return;
  if (show) sidebar.classList.remove("hidden");
  else sidebar.classList.add("hidden");
}

/**
 * Navega a una ruta (sin recargar la página).
 * routeName: "dashboard" | "events" | ... | "login"
 */
export async function mountRoute(routeName, { replace = false } = {}) {
  let target = routeName || DEFAULT_PUBLIC_ROUTE;

  // Redirección preventiva si está en modo "my_events" y no es dashboard/settings/login
  const role = await getUserRole().catch(() => NO_ACCESS_ROLE);
  const normalizedRole = normalizeRole(role);
  if (isSupplierOnly(normalizedRole) && target !== "login") target = "supplier";
  const activeMode = getState().ui?.preferences?.experienceMode || "management";
  const showOnlyViewerPages = normalizedRole === "viewer" || activeMode === "my_events";
  if (showOnlyViewerPages && target !== "login" && !["dashboard", "settings", "mobile", "supplier"].includes(target)) {
    console.warn(`[router] Redirecting from ${target} to dashboard in my_events mode.`);
    target = "dashboard";
  }

  const finalRoute = await applyAuthGuards(target);

  // evitar trabajo si ya estamos ahí (pero que la URL no se quede en una ruta denegada)
  if (_currentRoute === finalRoute) {
    if (getRouteFromLocation() !== finalRoute) history.replaceState({ route: finalRoute }, "", `#/${encodeURIComponent(finalRoute)}`);
    return;
  }

  _currentRoute = finalRoute;

  // actualizar URL (hash)
  const nextHash = `#/${encodeURIComponent(finalRoute)}`;
  if (replace) history.replaceState({ route: finalRoute }, "", nextHash);
  else history.pushState({ route: finalRoute }, "", nextHash);

  // UI nav
  setActiveNav(finalRoute);

  // Toggle sidebar visibility: hide on login, show otherwise
  toggleSidebarVisibility(finalRoute !== "login");

  // Set a data attribute on <body> for CSS rules (optional styling)
  document.body.dataset.route = finalRoute;

  // carga
  await loadPage(finalRoute);
}

/**
 * Inicializa router:
 * - listeners nav y popstate/hashchange
 * - monta ruta inicial
 */
export async function initRouter() {
  if (_isInitialized) return;
  _isInitialized = true;

  // Click en navegación (si existe data-route)
  document.addEventListener("click", (ev) => {
    const btn = ev.target.closest("[data-route]");
    // <body> también lleva data-route (lo usa el CSS de cada página) y no es un
    // botón de navegación: si se aceptara, todos los clics de la app acabarían
    // aquí con preventDefault (checkbox que no se marcan, enlaces y descargas
    // que no se abren, formularios que no se envían).
    if (!btn || btn === document.body) return;

    const route = btn.getAttribute("data-route");
    if (!route) return;

    ev.preventDefault();
    mountRoute(route).catch((e) => console.error("[router] mountRoute error", e));
  });

  // back/forward
  window.addEventListener("popstate", () => {
    const r = getRouteFromLocation() || DEFAULT_PUBLIC_ROUTE;
    mountRoute(r, { replace: true }).catch((e) => console.error("[router] popstate error", e));
  });

  // hash manual
  window.addEventListener("hashchange", () => {
    const r = getRouteFromLocation() || DEFAULT_PUBLIC_ROUTE;
    mountRoute(r, { replace: true }).catch((e) => console.error("[router] hashchange error", e));
  });

  // Si cambia auth (login/logout), re-evaluamos guard y montamos
  try {
    const supabase = getSupabaseClient();
    supabase.auth.onAuthStateChange(() => {
      const r = getRouteFromLocation() || DEFAULT_PUBLIC_ROUTE;
      mountRoute(r, { replace: true }).catch((e) => console.error("[router] authchange error", e));
    });
  } catch (e) {
    console.warn("[router] No se pudo suscribir a onAuthStateChange.", e);
  }

  // Ruta inicial
  const initial = getRouteFromLocation() || DEFAULT_PUBLIC_ROUTE;
  await mountRoute(initial, { replace: true });
}
